// Admin API Service - Connects to Azure Functions backend
const API_BASE_URL = 'https://insiderpass-api.azurewebsites.net/api';

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

export function getToken(): string | null {
  return localStorage.getItem('adminToken');
}

export function setToken(token: string): void {
  localStorage.setItem('adminToken', token);
}

export function clearToken(): void {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminStaff');
}

export function isAuthenticated(): boolean {
  const token = getToken();
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now() && payload.isAdmin === true;
  } catch {
    return false;
  }
}

export function getCurrentStaff(): Staff | null {
  const staffStr = localStorage.getItem('adminStaff');
  return staffStr ? JSON.parse(staffStr) : null;
}

export function isManager(): boolean {
  const staff = getCurrentStaff();
  return staff?.role === 'manager';
}

// ============================================================================
// API HELPER
// ============================================================================

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// TYPES
// ============================================================================

export interface Staff {
  staffId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'staff' | 'manager';
  storeLocation: string | null;
}

export interface Member {
  memberId: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  currentPoints: number;
  lifetimePoints: number;
  currentTier: 'Bronze' | 'Silver' | 'Gold';
  enrolledDate: string;
  lastActivityDate: string | null;
  shopifyCustomerId: string | null;
}

export interface MemberDetail extends Member {
  phoneCarrier: string | null;
  phoneType: string | null;
  auctionMethodUserId: string | null;
  createdDate: string;
  modifiedDate: string;
}

export interface CustomerMetrics {
  // Spending & Activity
  totalSpend: number;
  totalTransactions: number;
  avgTransactionValue: number | null;
  maxTransactionValue: number | null;
  firstTransactionDate: string | null;
  lastTransactionDate: string | null;
  avgDaysBetweenVisits: number | null;
  currentGapDays: number | null;

  // Channel Breakdown
  shopifyTransactions: number;
  auctionMethodTransactions: number;
  inStoreTransactions: number;
  primaryChannel: string | null;
  isMultiChannel: boolean;

  // Location & Timing
  uniqueLocationsVisited: number;
  preferredDayOfWeek: number | null;
  preferredHour: number | null;
  weekendTransactionPct: number | null;

  // Redemptions
  redemptionCount: number;
  lastRedemptionDate: string | null;
  pointsEverEarned: number;
  pointsEverRedeemed: number;

  // RFM & Segmentation
  rfmRecency: number | null;
  rfmFrequency: number | null;
  rfmMonetary: number | null;
  rfmScore: number | null;
  customerSegment: string | null;
  churnRiskScore: number | null;
}

export interface Redemption {
  redemptionId: string;
  redemptionCode: string;
  status: string;
  pointsRedeemed: number;
  requestedDate: string | null;
  confirmedDate: string | null;
  fulfilledDate: string | null;
  createdDate: string;
  rewardName: string;
  rewardCategory: string;
  member: {
    memberId: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
  };
}

export interface RedemptionValidation {
  success: boolean;
  valid: boolean;
  status: 'ready' | 'already_fulfilled' | 'cancelled' | 'expired';
  message: string;
  redemption: {
    redemptionId: string;
    redemptionCode: string;
    status: string;
    pointsRedeemed: number;
    reward: {
      name: string;
      description: string;
      category: string;
    };
    member: {
      memberId: string;
      phoneNumber: string;
      firstName: string;
      lastName: string;
      tier: string;
    };
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ============================================================================
// AUTH API
// ============================================================================

export async function login(
  email: string,
  password: string
): Promise<{ success: boolean; staff: Staff; token: string }> {
  const response = await apiRequest<{ success: boolean; staff: Staff; token: string }>('/staff/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (response.success && response.token) {
    setToken(response.token);
    localStorage.setItem('adminStaff', JSON.stringify(response.staff));
  }

  return response;
}

export function logout(): void {
  clearToken();
}

// ============================================================================
// MEMBERS API
// ============================================================================

export async function getMembers(params: {
  page?: number;
  limit?: number;
  search?: string;
  tier?: string;
} = {}): Promise<{ members: Member[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.search) searchParams.set('search', params.search);
  if (params.tier) searchParams.set('tier', params.tier);

  const query = searchParams.toString();
  return apiRequest(`/staff/members${query ? `?${query}` : ''}`);
}

export async function getMemberDetail(memberId: string): Promise<{
  member: MemberDetail;
  redemptions: Array<{
    redemptionId: string;
    rewardId: string;
    rewardName: string;
    pointsRedeemed: number;
    redemptionCode: string;
    status: string;
    requestedDate: string | null;
    confirmedDate: string | null;
    fulfilledDate: string | null;
    createdDate: string;
  }>;
  metrics: CustomerMetrics | null;
}> {
  return apiRequest(`/staff/members/${memberId}`);
}

// ============================================================================
// REDEMPTIONS API
// ============================================================================

export async function validateRedemption(redemptionCode: string): Promise<RedemptionValidation> {
  return apiRequest('/staff/redemption/validate', {
    method: 'POST',
    body: JSON.stringify({ redemptionCode }),
  });
}

export async function fulfillRedemption(redemptionCode: string): Promise<{ success: boolean; message: string }> {
  return apiRequest('/staff/redemption/fulfill', {
    method: 'POST',
    body: JSON.stringify({ redemptionCode }),
  });
}

export async function getRedemptions(params: {
  page?: number;
  limit?: number;
  status?: string;
} = {}): Promise<{ redemptions: Redemption[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.status) searchParams.set('status', params.status);

  const query = searchParams.toString();
  return apiRequest(`/staff/redemptions${query ? `?${query}` : ''}`);
}

// ============================================================================
// ACCOUNT HEALTH / VIOLATIONS API
// ============================================================================

export interface ViolationType {
  violationTypeId: string;
  name: string;
  description: string;
  healthScoreImpact: number;
  consequenceType: 'Warning' | 'Suspension' | 'Ban';
  suspensionDays: number | null;
  expiresAfterDays: number;
  isActive: boolean;
  createdDate: string;
  modifiedDate: string;
}

export interface MemberViolation {
  violationId: string;
  memberId: string;
  violationTypeId: string;
  violationName: string;
  violationDescription: string;
  consequenceType: string;
  healthScoreImpact: number;
  issuedByAdminId: string | null;
  issuedByName: string | null;
  notes: string | null;
  issuedDate: string;
  expiresDate: string;
  status: 'Active' | 'Expired' | 'Overturned';
  overturnedDate: string | null;
  overturnNotes: string | null;
}

export interface AccountHealth {
  score: number;
  status: string;
  isSuspended: boolean;
  suspendedUntil: string | null;
  isBanned: boolean;
  violations: MemberViolation[];
}

export async function getViolationTypes(includeInactive = false): Promise<{ violationTypes: ViolationType[] }> {
  const q = includeInactive ? '?includeInactive=true' : '';
  return apiRequest(`/staff/violations/types${q}`);
}

export async function createViolationType(data: Partial<ViolationType>): Promise<{ violationType: ViolationType }> {
  return apiRequest('/staff/violations/types', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateViolationType(id: string, data: Partial<ViolationType>): Promise<{ violationType: ViolationType }> {
  return apiRequest(`/staff/violations/types/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function getMemberHealth(memberId: string): Promise<AccountHealth> {
  return apiRequest(`/staff/members/${memberId}/violations`);
}

export async function issueViolation(memberId: string, violationTypeId: string, notes?: string): Promise<{ health: AccountHealth }> {
  return apiRequest(`/staff/members/${memberId}/violations`, {
    method: 'POST',
    body: JSON.stringify({ violationTypeId, notes })
  });
}

export async function overturnViolation(memberId: string, violationId: string, notes?: string): Promise<{ health: AccountHealth }> {
  return apiRequest(`/staff/members/${memberId}/violations/${violationId}/overturn`, {
    method: 'POST',
    body: JSON.stringify({ notes })
  });
}

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export const api = {
  login,
  logout,
  isAuthenticated,
  getCurrentStaff,
  isManager,
  getMembers,
  getMemberDetail,
  validateRedemption,
  fulfillRedemption,
  getRedemptions,
};

export default api;
